import { Box, CircularProgress, Stack } from "@mui/material";
import { useUnit } from "effector-react";
import { useEffect, useRef, useState } from "react";

import images from "../constants/images";
import { STEP_TUTORIAL, type StepTutorialTypes } from "../constants/tutorial";
import { $playerData, $playerValleyMap } from "../store";
import type {
  ResourceObjects,
  TokenValleyBlockType,
} from "../types/tokenValley";
import { managerTutorialStep } from "../utils/managerTutorialStep";
import Block from "./Block";
import InfoBlockButton from "./InfoBlockButton";
import ResourceGatheringButton from "./ResourceGatheringButton";
import ShipDialog from "./ShipDialog";
import { useTutorial } from "./hooks/useTutorial";
import MoveBuilding from "./ui/MoveBuilding";

const Blocks = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [playerValley, playerData] = useUnit([$playerValleyMap, $playerData]);
  const [stepTutorial, setStepTutorial] = useState<StepTutorialTypes>(
    STEP_TUTORIAL.INIT,
  );

  const [resourceGathering, setResourceGathering] =
    useState<ResourceObjects>("resource/void");
  const [resourceGatheringButton, setResourceGatheringButton] =
    useState<ResourceObjects>("resource/void");
  const { showTutorial } = useTutorial();
  const [moveMode, setMoveMode] = useState<boolean>(false);
  const [activeBlock, setActiveBlock] = useState<TokenValleyBlockType | null>(
    null,
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  useEffect(() => {
    managerTutorialStep({
      playerDataTutorialStep: playerData?.tutorial_step,
      stepTutorial,
      showTutorial,
    });
  }, [playerData?.tutorial_step, stepTutorial]);

  const splitTiles = [];
  let i = 0;

  while (i < playerValley.length) {
    splitTiles.push(playerValley.slice(i, i + 2));
    i += 2;
    if (i < playerValley.length) {
      splitTiles.push(playerValley.slice(i, i + 3));
      i += 3;
    }
  }

  return (
    <>
      {((activeBlock && playerData?.tutorial_step === -2) ||
        (moveMode && playerData?.tutorial_step === -2)) && (
        <Stack
          width={"100%"}
          bottom={"2rem"}
          display={"flex"}
          position={"absolute"}
          alignItems={"center"}
        >
          <Stack
            p={1}
            gap={1}
            zIndex={1000}
            height={"65px"}
            direction={"row"}
            border={"2px solid"}
            width={"fit-content"}
            borderRadius={"20px"}
            bgcolor={"primary.bgBlockMenu"}
            borderColor={"primary.bgBlockMenuBorder"}
          >
            {activeBlock && activeBlock.block_type !== "build/void" && (
              <InfoBlockButton activeBlock={activeBlock} />
            )}
            {!moveMode &&
              activeBlock &&
              activeBlock.block_time_ready < new Date().getTime() &&
              activeBlock?.block_type !== "build/void" &&
              activeBlock?.block_type !== "build/token_house" &&
              activeBlock?.block_type !== "build/builders_hut" &&
              activeBlock?.block_type !== "build/lab" && (
                <ResourceGatheringButton
                  activeBlock={activeBlock}
                  setResourceGatheringButton={setResourceGatheringButton}
                />
              )}

            <MoveBuilding
              moveMode={moveMode}
              setMoveMode={setMoveMode}
              setActiveBlock={setActiveBlock}
              playerDataTutorialStep={playerData?.tutorial_step}
            />
          </Stack>
        </Stack>
      )}
      <Stack
        ref={containerRef}
        position="relative"
        width="100%"
        height="102%"
        bgcolor={"#569d42"}
        alignItems="center"
        sx={{
          overflowY: "auto",
          overflowX: "hidden",
          scrollbarWidth: "none",
          overscrollBehavior: "none",
          touchAction: "manipulation",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Box width={"100%"} position={"absolute"}>
          <img src={images.background} alt="background" width={"100%"} />
          <ShipDialog />
        </Box>
        {splitTiles.length > 0 ? (
          <Stack position="relative">
            {splitTiles.map((row, index) => {
              const tileSize = 68;
              const spacingY = tileSize * 0.431;
              const spacingX = tileSize * 1.42;

              const top = index * spacingY + 190;
              return row.map((block, indexInRow) => {
                let left = indexInRow * spacingX;
                if (index % 2 === 0) {
                  left += spacingX - 49;
                }

                return (
                  <Block
                    top={top}
                    left={left}
                    index={index}
                    block={block}
                    moveMode={moveMode}
                    key={block.block_uuid}
                    activeBlock={activeBlock}
                    stepTutorial={stepTutorial}
                    setActiveBlock={setActiveBlock}
                    setStepTutorial={setStepTutorial}
                    resourceGathering={resourceGathering}
                    playerDataStep={playerData?.tutorial_step}
                    setResourceGathering={setResourceGathering}
                    resourceGatheringButton={resourceGatheringButton}
                  />
                );
              });
            })}
          </Stack>
        ) : (
          <CircularProgress
            sx={{
              position: "absolute",
              top: "50%",
              left: "40%",
              transform: "translate(-50%, -50%)",
              color: "green",
            }}
            size={100}
          />
        )}
      </Stack>
    </>
  );
};

export default Blocks;
